import { createContext, useContext, useState, useMemo, useCallback, ReactNode } from 'react';

type ThemeName = 'default' | 'green' | 'orange' | 'purple';

interface Theme {
  name: ThemeName;
  colorClass: string;
  colorText: string;
  colorHover: string;
  tableHeaderClass: string;
}

const themes: Record<ThemeName, Theme> = {
  default: {
    name: 'default',
    colorClass: 'bg-primary hover:bg-secondary',
    colorText: 'text-primary bg-active hover:bg-active',
    colorHover: 'hover:bg-active',
    tableHeaderClass: 'bg-active',
  },
  green: {
    name: 'green',
    colorClass: 'bg-green-alurkerja hover:bg-green-alurkerja-hovered',
    colorText: 'text-green-alurkerja bg-green-sidebar',
    colorHover: 'hover:bg-green-sidebar',
    tableHeaderClass: 'bg-green-table',
  },
  orange: {
    name: 'orange',
    colorClass: 'bg-orange-alurkerja hover:bg-orange-alurkerja-hovered',
    colorText: 'text-orange-alurkerja bg-orange-sidebar',
    colorHover: 'hover:bg-orange-sidebar',
    tableHeaderClass: 'bg-orange-table',
  },
  purple: {
    name: 'purple',
    colorClass: 'bg-purple-alurkerja hover:bg-purple-alurkerja-hovered',
    colorText: 'text-purple-alurkerja bg-purple-sidebar',
    colorHover: 'hover:bg-purple-sidebar',
    tableHeaderClass: 'bg-purple-table',
  },
};

interface ThemeContextValue {
  theme: Theme;
  setTheme: (name: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: themes.default,
  setTheme: () => {},
});

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = (typeof window !== 'undefined' ? localStorage.getItem('tenant_theme') : 'default') as ThemeName;
    return themes[saved] ?? themes.default;
  });

  const handleSetTheme = useCallback((name: ThemeName) => {
    localStorage.setItem('tenant_theme', name);
    setTheme(themes[name]);
  }, []);

  const contextValue = useMemo(() => ({ theme, setTheme: handleSetTheme }), [theme, handleSetTheme]);

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
